import Swal from "sweetalert2";

const ICON_BADGES = {
  question: {
    wrap: "bg-indigo-50 text-indigo-600 ring-indigo-100",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>`,
  },
  warning: {
    wrap: "bg-amber-50 text-amber-600 ring-amber-100",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>`,
  },
  error: {
    wrap: "bg-red-50 text-red-600 ring-red-100",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`,
  },
  success: {
    wrap: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>`,
  },
  info: {
    wrap: "bg-sky-50 text-sky-600 ring-sky-100",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
  },
};

const BADGE_BASE =
  "inline-flex items-center justify-center w-[3.375rem] h-[3.375rem] rounded-xl shrink-0 shadow-sm ring-1 ring-inset";

function iconBadgeHtml(type = "info") {
  const badge = ICON_BADGES[type] || ICON_BADGES.info;
  return `<div class="${BADGE_BASE} ${badge.wrap}">${badge.svg}</div>`;
}

const TW = {
  container: "!p-4 backdrop-blur-sm",
  popup:
    "relative !w-[28rem] max-w-[calc(100vw-2rem)] !p-0 !pt-6 !px-6 rounded-[1.125rem] border border-slate-200 bg-white shadow-2xl overflow-hidden font-sans gap-0 box-border focus:outline-none",
  header: "!p-0 !m-0 flex flex-col items-center gap-0",
  title:
    "font-display text-lg font-semibold leading-snug tracking-tight text-slate-900 !p-0 !mb-2",
  htmlContainer:
    "text-[0.9375rem] leading-relaxed text-slate-500 !m-0 !p-0 font-normal",
  icon:
    "swal-icon-custom !flex !items-center !justify-center !border-0 !w-auto !h-auto !mx-auto !mb-3 !mt-0 !p-0",
  actions:
    "!w-[calc(100%+3rem)] !-mx-6 !mt-5 !px-6 !py-4 !pb-5 gap-2.5 border-t border-slate-100 bg-slate-50 box-border",
  actionsDual: "grid grid-cols-2",
  actionsSingle: "flex",
  confirm:
    "inline-flex items-center justify-center w-full min-h-10 px-4 py-2 rounded-lg border border-indigo-700 bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700 active:scale-[0.985] transition-all duration-150 !m-0 outline-none focus:outline-none focus:ring-0 cursor-pointer",
  confirmDanger:
    "inline-flex items-center justify-center w-full min-h-10 px-4 py-2 rounded-lg border border-red-700 bg-red-600 text-white text-sm font-semibold shadow-sm hover:bg-red-700 active:scale-[0.985] transition-all duration-150 !m-0 outline-none focus:outline-none focus:ring-0 cursor-pointer",
  cancel:
    "inline-flex items-center justify-center w-full min-h-10 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold shadow-sm hover:bg-slate-50 hover:text-slate-700 hover:border-slate-300 active:scale-[0.985] transition-all duration-150 !m-0 outline-none focus:outline-none focus:ring-0 cursor-pointer",
};

function addAccentBar(popup) {
  if (!popup || popup.querySelector("[data-swal-accent]")) return;
  const bar = document.createElement("div");
  bar.dataset.swalAccent = "true";
  bar.className =
    "absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-300 pointer-events-none";
  popup.prepend(bar);
}

const BASE_CONFIG = {
  padding: 0,
  buttonsStyling: false,
  customClass: {
    container: TW.container,
    popup: TW.popup,
    header: TW.header,
    title: TW.title,
    htmlContainer: TW.htmlContainer,
    icon: TW.icon,
    actions: TW.actions,
    confirmButton: TW.confirm,
    cancelButton: TW.cancel,
  },
  backdrop: "rgba(15, 23, 42, 0.4)",
  focusConfirm: false,
  focusCancel: false,
  showClass: { popup: "animate-slide-up" },
  didOpen: addAccentBar,
};

const swal = Swal.mixin(BASE_CONFIG);

function isDangerConfirm(confirmText, icon) {
  const label = (confirmText || "").toLowerCase();
  return (
    icon === "warning" &&
    (label.includes("delete") ||
      label.includes("remove") ||
      label.includes("close") ||
      label.includes("dismiss"))
  );
}

function fireOptions({ icon = "info", showCancelButton = false, danger = false }) {
  const confirmBtn = danger ? TW.confirmDanger : TW.confirm;
  return {
    icon,
    iconHtml: iconBadgeHtml(icon),
    showCancelButton,
    customClass: {
      container: TW.container,
      popup: TW.popup,
      header: TW.header,
      title: TW.title,
      htmlContainer: TW.htmlContainer,
      icon: TW.icon,
      actions: `${TW.actions} ${showCancelButton ? TW.actionsDual : TW.actionsSingle}`,
      confirmButton: showCancelButton ? confirmBtn : `${confirmBtn} flex-1 w-full`,
      cancelButton: TW.cancel,
    },
  };
}

/** Replacement for window.confirm — returns true if user confirms. */
export async function confirmAction({
  title = "Are you sure?",
  text = "",
  confirmText = "Yes",
  cancelText = "Cancel",
  icon = "warning",
} = {}) {
  const danger = isDangerConfirm(confirmText, icon);
  const result = await swal.fire({
    title,
    text,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    ...fireOptions({ icon, showCancelButton: true, danger }),
  });
  return result.isConfirmed;
}

/** Replacement for window.alert — informational or result messages. */
export function showAlert({
  title = "",
  text = "",
  icon = "info",
  confirmText = "OK",
} = {}) {
  return swal.fire({
    title,
    text,
    confirmButtonText: confirmText,
    ...fireOptions({ icon, showCancelButton: false }),
  });
}

export function showError(message, title = "Error") {
  return showAlert({ title, text: message, icon: "error" });
}

export function showWarning(message, title = "Warning") {
  return showAlert({ title, text: message, icon: "warning" });
}

export function showInfo(message, title = "") {
  return showAlert({ title: title || "Notice", text: message, icon: "info" });
}

export function showSuccess(message, title = "Success") {
  return showAlert({ title, text: message, icon: "success" });
}
